import { Metric, PredictionGraphBox } from '@dto/pageResponse.dto';
import { PredictionDTO, PredictionModel, TimeseriesItem } from '@dto/timeseriesResponse.dto';
import { UnitUtil } from '@dto/util/UnitUtil';
import { ColorMap } from '@parts/graph/AbstractGraphPart';
import { MenuProps, Modal } from 'antd';
import { ItemType } from 'antd/es/menu/interface';
import { CompareFn } from 'antd/es/table/interface';
import type { Config, Layout, PlotRelayoutEvent, ScatterData } from 'plotly.js';
import Plotly from 'plotly.js/lib/core';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { FaExpand } from 'react-icons/fa';
import { useResizeDetector } from 'react-resize-detector';
import { headerControlsWidth } from 'src/util/columnWidths';
import { GLOB } from 'src/util/Glob';
import { cellBorderPx, cellPaddingPx } from 'src/util/measureColumns';
import { TextUtil } from 'src/util/TextUtil';
import Plot from '../../PlotlyFactory';
import { XmHelp } from '../help/XmHelp';
import { XmSpinner } from '../spinner/XmSpinner';
import { XmTableScope } from '../table/XmTable';
import './graph.less';
import { GraphBtns } from './GraphBtns';
import { GraphPlotUtil } from './GraphPlotUtil';
import { colorSquare } from './GraphTable';
import { GraphType, KTM, LayoutProps, PlotEl, PropsKTM, createPlotlyLayout } from './GraphTypes';
import { COLOR_SQUARE_WIDTH } from './XmColorSquare';

interface XmPredictionGraphProps extends PredictionDTO {
  box: PredictionGraphBox;
  type: GraphType;
  tabName: string;
}

interface PredKTMProps extends PropsKTM {
  real: boolean;
  visible: boolean | 'legendonly';
  meta: Record<string, unknown>;
}

function predSorter(key: string): CompareFn<KTM<PredKTMProps>> {
  return function ktmCompare(a, b) {
    if (a.props.real || b.props.real) return 0;
    return GLOB.naturalSort(a.props.meta[key], b.props.meta[key]);
  };
}

export const XmPredictionGraph: FC<XmPredictionGraphProps> = (props) => {
  const [tscope] = useState(() => new XmTableScope<KTM<PredKTMProps>>('plain'));
  const colorMap = useRef<Record<string, string>>({});
  const [modalFullscreenVisible, setModalFullscreenVisible] = useState(false);
  const [data, setData] = useState<Partial<ScatterData>[]>([]);
  const [tableData, setTableData] = useState<KTM<PredKTMProps>[]>([]);
  const [plotVisible, setPlotVisible] = useState(true);
  const maxVal = useRef(0);
  const plotlyRef = useRef<PlotEl>(undefined);

  const [onClick] = useState((): React.MouseEventHandler<HTMLDivElement> =>
    props.type === GraphType.dashboard ? () => setModalFullscreenVisible(true) : undefined);

  const [dropdownItems] = useState<MenuProps>(() => {
    const items: ItemType[] = [];
    if (props.type === GraphType.regular) {
      items.push({
        key: 'fullscreen',
        icon: <FaExpand />,
        label: 'Fullscreen',
        title: 'Show graph in fullscreen',
        onClick: () => setModalFullscreenVisible(true)
      });
    }
    return { items };
  });

  const [config] = useState<Partial<Config>>(() => ({
    displaylogo: false, autosizable: true, responsive: true, displayModeBar: props.type !== GraphType.dashboard,
    modeBarButtons: [['resetScale2d']],
  }));

  const [layoutProps] = useState<LayoutProps>(() => ({ ...props, box: { ...props.box, metrics: [props.box.metric] } }));

  const [layout] = useState<Partial<Layout>>(() => {
    const l = createPlotlyLayout(layoutProps);
    l.xaxis.fixedrange = true;
    l.height = 300;
    l.yaxis.rangemode = 'nonnegative';
    return l;
  });

  const onResize = useCallback(() => {
    if (!plotlyRef.current?._fullData?.length)
      return;
    Plotly.Plots.resize(plotlyRef.current);
  }, []);
  const { ref } = useResizeDetector<HTMLDivElement>({ onResize, skipOnMount: true });

  useEffect(() => {
    if (Object.keys(props.real).length || Object.keys(props.models).length)
      updateData();
  }, []);

  const assignTickFormat = (tblData: KTM[], plotData?: Partial<ScatterData>[]) =>
    GraphPlotUtil.assignTickFormat(tblData, plotData || data, layoutProps, layout, maxVal.current);

  function createPlotData(item: TimeseriesItem | PredictionModel, metric: string, tableData: Record<string, KTM<PredKTMProps>>) {
    const meta = item.metadata;
    const metricObj = [props.box.metric, ...props.box.other_metrics].find(m => m.metric === metric);
    const name: string = meta['label'] || metricObj?.label || metric;
    const uid = name;
    const otherName = GraphPlotUtil.SINGLE_ITEM;

    const y: number[] = Object.values(item.timeseries);
    const pd: Partial<ScatterData> = {
      x: GraphPlotUtil.timeAxis(meta),
      y,
      type: 'scatter',
      name,
      line: { color: metricObj?.color ?? colorMap.current[metric], width: 1 },
      meta: { uid, itemName: meta['name'] || meta['label'], metric: metricObj?.label || metric },
      visible: meta['display'] !== false || 'legendonly',
    };
    GraphPlotUtil.assignHoverTemplate(pd, layout.hovermode);

    const metro = metricObj || props.box.metric;
    const isBi = UnitUtil.isBiUnit(metro?.unit);
    pd.customdata = y.map(v => v == null ? null : UnitUtil.formatSI(Math.abs(v), 2, isBi, metro));

    let ktm = tableData[uid];
    if (!ktm) {
      ktm = new KTM();
      tableData[uid] = ktm;
      ktm.key = metric;
      ktm.name = name;
      ktm.metric = metric;
      ktm.uid = uid;
      ktm.props = { visible: pd.visible, meta: {} };
      for (const pm of props.box.metadata) {
        ktm.props.meta[pm.key] = meta[pm.key];
      }
    }
    ktm.data[otherName] = { ...item.metadata, name: meta['name'] || meta['label'] };
    ktm.rendered[otherName] = {};

    return pd;
  }

  function updateData() {
    const colors = new ColorMap();
    for (const metric of [props.box.metric, ...props.box.other_metrics]) {
      if (metric.color) {
        colors.colorPalette[metric.metric] = metric.color;
      }
    }
    GraphPlotUtil.assignUniqueColors([...Object.keys(props.real), ...Object.keys(props.models)], colors);
    colorMap.current = colors.colorPalette;

    const plotData: Partial<ScatterData>[] = [];
    const tableData: Record<string, KTM<PredKTMProps>> = {};

    for (const key in props.real) {
      if (Object.hasOwn(props.real, key)) {
        plotData.push(createPlotData(props.real[key], key, tableData));
      }
    }

    const modelTable: Record<string, KTM<PredKTMProps>> = {};
    const sortedModels = Object.entries(props.models).sort((a) => a[0].includes('trend') ? 1 : -1);
    for (const [key, model] of sortedModels) {
      plotData.push(createPlotData(model, key, modelTable));
    }

    const x0 = Math.min(Number.MAX_SAFE_INTEGER, ...plotData.map(pd => pd.x[0] as number));
    const x1 = Math.max(0, ...plotData.map(pd => pd.x.at(-1) as number));
    const totalTable: Record<string, KTM<PredKTMProps>> = {};
    const tsi = Object.values(props.real)[0];
    if (props.box.max && tsi) {
      const label = 'Total available';
      const metric: Metric = { ...props.box.metric, metric: 'totalcapacity', label, order: 0, stacked: false, type: 'line', color: 'black' };
      props.box.other_metrics.push(metric);
      layout.yaxis.range = [0, props.box.max * 1.3];
      const start = x0 / 1000;
      const end = x1 / 1000;
      const pd = createPlotData({
        metadata: { ...tsi.metadata, display: true, label, name: 'Max value', interval: (end - start) / 60 / 2 - 1, metric: metric.metric, start, end, description: null },
        timeseries: [props.box.max, props.box.max, props.box.max]
      }, metric.metric, totalTable);
      pd.mode = 'lines';
      plotData.push(pd);
    }

    const realTable = [...Object.values(tableData), ...Object.values(totalTable)];
    for (const row of realTable) {
      row.props.real = true;
    }
    const lastReal = realTable.at(-1);
    if (lastReal)
      lastReal.props.separator = true;

    layout.shapes = [];
    if (props.box.lowerMax)
      layout.shapes.push({
        type: 'line',
        y0: props.box.lowerMax,
        y1: props.box.lowerMax,
        x0, x1,
        line: { width: 1, dash: 'dash' },
      });

    const sortedTable = [...realTable, ...Object.values(modelTable).sort((a) => a.props.visible ? 1 : -1)];

    maxVal.current = Math.max(props.box.max, GraphPlotUtil.maxY(plotData));
    assignTickFormat(sortedTable, plotData);
    setData(plotData);
    setTableData(sortedTable);
  }

  function onRelayout(event: Readonly<PlotRelayoutEvent>) {
    if (event.autosize || !event.hovermode) return;
    layout.hovermode = event.hovermode;
    data.forEach(pd => GraphPlotUtil.assignHoverTemplate(pd, layout.hovermode));
    setData([...data]);
  }

  return (
    <>
      <div ref={ref} className="xm-graph xm-prediction-graph" onClick={onClick}>
        {props.box.help && <XmHelp href={props.box.help.url} title={props.box.help.text} />}

        {props.type !== GraphType.dashboard &&
          <GraphBtns dropdownItems={dropdownItems} layout={layout} refresh={() => setData(prev => [...prev])} modebarBtns={config.modeBarButtons as [][]} />}

        {plotVisible ? <Plot ref={r => { plotlyRef.current = r; }}
          className="xm-plotly js-plotly-plot"
          data={data} layout={{ ...layout }} onRelayout={onRelayout}
          useResizeHandler config={config} />
          : <div style={{ height: layout.height }}><XmSpinner /></div>}

        {props.type !== GraphType.dashboard &&
          <tscope.XmTable
            key="graph-grid"
            dataSource={tableData}
            rowClassName={record => record.props.separator ? 'xm-tr-separator' : ''}
            scroll={{ y: '140px' }}
            pagination={false}
            bordered
          >
            {[
              <tscope.Col key="metricColor" className="square-td" width={COLOR_SQUARE_WIDTH}
                render={(value, record) => colorSquare(record, undefined, { data, tableData, setData, setTableData, setPlotVisible, assignTickFormat })} />,

              <tscope.Col
                key="name"
                className="xm-td-ellipsis"
                title="Name"
                dataIndex="name"
                width="auto"
                render={value => <div className="xm-ellipsis"><div>{value}</div></div>}
              />,

              ...props.box.metadata.map(pm => <tscope.Col key={pm.key}
                title={pm.label}
                dataIndex={['props', 'meta', pm.key]}
                align={pm.number ? 'right' : undefined}
                sorter={predSorter(pm.key)}
                width="auto"
                // `width="auto"` makes the engine skip these columns, so the floor is built here.
                minWidth={TextUtil.measureText(pm.label, 'max-content', ref.current?.querySelector('th') || ref.current) * 0.9
                  + cellPaddingPx() + cellBorderPx() + headerControlsWidth({ hasTitle: true, hasSort: true })}
              />),
            ]}
          </tscope.XmTable>}
      </div>

      {props.type !== GraphType.modal &&
        <Modal centered open={modalFullscreenVisible} onCancel={() => setModalFullscreenVisible(false)} destroyOnHidden
          closable={false} footer={null} width="90%">
          <XmPredictionGraph {...props} type={GraphType.modal}
            box={{ ...props.box, graphLabel: GraphPlotUtil.fullScreenTitle(props.tabName, props.box.graphLabel, props.type === GraphType.dashboard) }} />
        </Modal>}
    </>
  );
};
